import React from 'react';
import PropTypes from 'prop-types';
import { cloneObject, focusFirstElement, manageEventListener } from '../../../common/utils';
import FormikInput from './_FormikInput';

const checkedSelector = ':checked';

class FormikRadioGroup extends React.Component {
  constructor(props) {
    super(props);

    const { initialValue = null } = props;

    this.state = {
      initialValue,
      currentValue: initialValue
    };

    this.refRadioGroup = React.createRef();
    this.afterComponentUpdate = this.afterComponentUpdate.bind(this);
    this.manageLabelClickEvent = this.manageLabelClickEvent.bind(this);
    this.focusRadioButton = this.focusRadioButton.bind(this);
  }

  componentDidMount() {
    this.manageLabelClickEvent('add');
  }

  componentDidUpdate() {
    this.afterComponentUpdate();
  }

  componentWillUnmount() {
    this.manageLabelClickEvent('remove');
  }

  afterComponentUpdate() {
    const {
      props: {
        onChange
      },
      state: {
        currentValue
      },
      refRadioGroup
    } = this;

    const { current: radioGroupElement } = refRadioGroup;
    const selectedOption = radioGroupElement.querySelector(checkedSelector);

    if (selectedOption) {
      const selectedOptionValue = selectedOption.getAttribute('value');

      if (selectedOptionValue !== currentValue) {
        this.setState({ currentValue: selectedOptionValue }, () => {
          if (onChange) {
            onChange(selectedOptionValue);
          }
        });
      }
    }
  }

  focusRadioButton() {
    const {
      refRadioGroup: {
        current: thisRadioGroup
      }
    } = this;

    const selectedRadioButton = thisRadioGroup.querySelector(checkedSelector);

    if (selectedRadioButton) {
      focusFirstElement(thisRadioGroup, [checkedSelector]);
      return;
    }

    focusFirstElement(thisRadioGroup);
  }

  manageLabelClickEvent(action) {
    const {
      props: {
        labelledById
      },
      focusRadioButton
    } = this;

    if (labelledById) {
      const labelElement = document.getElementById(labelledById);

      if (labelElement) {
        labelElement.classList.add('cursor-default');
        manageEventListener(labelElement, 'click', action, focusRadioButton);
      }
    }
  }

  render() {
    const {
      props: {
        fieldName,
        legend,
        title,
        labelledById,
        radioInputs,
        classNames = [],
        isSwappedAlign
      },
      state: {
        initialValue
      },
      refRadioGroup
    } = this;

    const fieldsetProps = {};

    if (labelledById) {
      fieldsetProps['aria-labelledby'] = labelledById;
    }

    else {
      fieldsetProps.title = title;
    }

    if (classNames.length > 0) {
      fieldsetProps.className = classNames.join(' ');
    }

    return (
      <fieldset {...fieldsetProps} ref={refRadioGroup}>
        {legend ? <legend>{legend}</legend> : null}
        {radioInputs.map(iteratedInputAttributes => {
          const inputAttributes = cloneObject(iteratedInputAttributes);
          const { value } = inputAttributes;
          if (value === initialValue) {
            inputAttributes.checked = true;
          }

          return (
            <FormikInput
              {...inputAttributes}
              key={`radioButton-${fieldName}-${value}`}
              name={fieldName}
              type='radio'
              isSwappedAlign={isSwappedAlign}
            />
          );
        })}
      </fieldset>
    );
  }
}

FormikRadioGroup.propTypes = {
  fieldName: PropTypes.string.isRequired,
  title(props, propName, componentName) {
    const {
      title,
      labelledById,
      legend
    } = props;

    if (!title && !labelledById && !legend) {
      return new Error(`Readable title for "fieldset" is missing in ${componentName}`);
    }

    if (title && typeof (title) !== 'string') {
      return new Error(
        `Invalid prop "${propName}": expected "string" but ${typeof (title)} was provided - in ${
          componentName
        }`
      );
    }
  },
  labelledById: PropTypes.string,
  legend: PropTypes.string,
  classNames: PropTypes.arrayOf(PropTypes.string),
  initialValue: PropTypes.string,
  isSwappedAlign: PropTypes.bool,
  onChange: PropTypes.func,
  radioInputs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired, // for aria-labels
      label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.bool
      ]).isRequired
    })
  ).isRequired
};

export default FormikRadioGroup;